import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { usePlant } from "@/context/PlantContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Truck, Factory, ClipboardList, AlertTriangle, PackageOpen, Activity,
  Calendar as CalIcon, Pencil, CheckCircle2, Sparkles, DollarSign, Bell, Radio, Layers,
} from "lucide-react";
import { toast } from "sonner";
import { mixColor } from "@/lib/mixColor";
import { localToday, shiftLocalDate } from "@/lib/dates";
import LiveKpiBar from "@/components/LiveKpiBar";
import AIDispatchPanel from "@/components/AIDispatchPanel";

const KpiCard = ({ icon: Icon, label, value, sub, testid, tone = "brand" }) => {
  // Tone → colored icon halo + accent for the number when critical/warning
  const toneMap = {
    brand:    { halo: "bg-primary-orange/15 border-primary-orange/30 text-primary-orange", num: "text-ink" },
    good:     { halo: "bg-emerald-500/15 border-emerald-500/30 text-emerald-500",         num: "text-ink" },
    warn:     { halo: "bg-amber-500/15 border-amber-500/30 text-amber-500",               num: "text-amber-500" },
    critical: { halo: "bg-red-500/15 border-red-500/30 text-red-500",                     num: "text-red-500" },
    info:     { halo: "bg-sky-500/15 border-sky-500/30 text-sky-500",                     num: "text-ink" },
  };
  const t = toneMap[tone] || toneMap.brand;
  return (
    <Card
      data-testid={testid}
      data-tone={tone}
      className="card-v2 p-5 kpi-hover transition-all"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="label-tag">{label}</div>
          <div className={`mono text-3xl md:text-4xl font-bold tracking-tight mt-2 ${t.num}`}>{value}</div>
          {sub && <div className="text-xs text-muted-industrial mt-1 truncate">{sub}</div>}
        </div>
        <div className={`w-11 h-11 rounded-lg border flex items-center justify-center shrink-0 ${t.halo}`}>
          <Icon className="w-5 h-5" strokeWidth={2} />
        </div>
      </div>
    </Card>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    operational: "border-primary-orange text-primary-orange bg-[rgba(245,124,0,0.06)]",
    idle: "border-industrial text-muted-industrial",
  };
  return (
    <span data-testid="plant-status-pill" className={`mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-md border ${map[status] || "border-industrial"}`}>
      ● {status}
    </span>
  );
};

export default function Dashboard() {
  const today = localToday();
  const { plantId } = usePlant();
  const [viewDate, setViewDate] = useState(today);
  const [data, setData] = useState(null);
  const [editJob, setEditJob] = useState(null);
  const [editForm, setEditForm] = useState({ date: "", time_required: "", quantity_m3: 0, po_number: "", extras: "" });

  const load = useCallback(() => {
    const q = new URLSearchParams({ date: viewDate });
    if (plantId) q.set("plant_id", plantId);
    api.get(`/dashboard?${q.toString()}`).then((r) => setData(r.data)).catch(() => {});
  }, [viewDate, plantId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const shiftDate = (delta) => setViewDate(shiftLocalDate(viewDate, delta));

  const openEdit = (j) => {
    setEditJob(j);
    setEditForm({
      date: j.date || today,
      time_required: j.time_required || "",
      quantity_m3: j.quantity_m3,
      po_number: j.po_number || "",
      extras: j.extras || "",
    });
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/jobs/${editJob.id}`, editForm);
      toast.success("Order updated");
      setEditJob(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const closeJob = async (j) => {
    try {
      await api.patch(`/jobs/${j.id}`, { status: "completed" });
      toast.success(`Order ${j.customer_name} closed`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (!data)
    return <div className="p-8 label-tag text-primary-orange pulse-orange">Loading ArmorBatch Global…</div>;

  const jobs = data.jobs || [];
  const sortedJobs = [...jobs].sort((a, b) => {
    const ac = a.status === "completed" ? 1 : 0;
    const bc = b.status === "completed" ? 1 : 0;
    if (ac !== bc) return ac - bc;
    return (a.time_required || "").localeCompare(b.time_required || "");
  });

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="dashboard-page">
      {/* v2.1 Live KPI bar — persistent top strip */}
      <LiveKpiBar
        activeTrucks={data.active_trucks}
        productionM3={data.production_m3}
        plantStatus={data.plant_status}
      />

      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-tag">ARMORBATCH GLOBAL · OVERVIEW</div>
          <h1 className="heading text-3xl sm:text-4xl font-black uppercase tracking-tight">
            Operations <span className="text-primary-orange">Dashboard</span> · <span className="text-primary-orange text-2xl sm:text-3xl mono">{viewDate}</span>
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 border border-industrial px-2 py-1 bg-surface rounded-lg">
            <button data-testid="dash-date-prev" onClick={() => shiftDate(-1)} className="text-muted-industrial hover:text-primary-orange px-1" aria-label="Previous day">‹</button>
            <CalIcon className="w-3.5 h-3.5 text-primary-orange" />
            <Input
              type="date"
              data-testid="dash-date-picker"
              value={viewDate}
              onChange={(e) => setViewDate(e.target.value)}
              className="rounded-none bg-transparent border-0 mono h-7 px-2 w-[145px] text-xs focus-visible:ring-0"
            />
            <button data-testid="dash-date-next" onClick={() => shiftDate(1)} className="text-muted-industrial hover:text-primary-orange px-1" aria-label="Next day">›</button>
            {viewDate !== today && (
              <button data-testid="dash-date-today" onClick={() => setViewDate(today)} className="mono text-[10px] uppercase text-primary-orange hover:underline pl-2 border-l border-industrial ml-1">Today</button>
            )}
          </div>
          <StatusPill status={data.plant_status} />
        </div>
      </div>

      {/* v2.1 KPI grid — colour-coded, animated on hover */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <KpiCard testid="kpi-orders" icon={ClipboardList} label="Orders Today" value={data.jobs_today} sub={`${data.jobs_remaining} still open`} tone="info" />
        <KpiCard testid="kpi-active-trucks" icon={Truck} label="Trucks Running" value={`${data.active_trucks}/${data.total_trucks}`} sub="On the road right now" tone={data.active_trucks > 0 ? "good" : "brand"} />
        <KpiCard testid="kpi-production" icon={Factory} label="Concrete Produced" value={`${data.production_m3} m³`} sub={viewDate === today ? "Loaded today" : `Loaded ${viewDate}`} tone="brand" />
        <KpiCard testid="kpi-revenue" icon={DollarSign} label="Revenue Today" value={`$${(data.revenue_today ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`} sub="Ex-GST · delivered dockets" tone="good" />
        <KpiCard testid="kpi-jobs-remaining" icon={Layers} label="Jobs Remaining" value={data.jobs_remaining} sub={`of ${data.jobs_today} ${viewDate === today ? "today" : "for " + viewDate}`} tone={data.jobs_remaining > 5 ? "warn" : "info"} />
        <KpiCard testid="kpi-alerts" icon={AlertTriangle} label="Dispatch Alerts" value={data.inventory_alerts.length} sub="Materials below threshold" tone={data.inventory_alerts.length > 0 ? "critical" : "good"} />
        <KpiCard testid="kpi-fleet-idle" icon={Radio} label="Fleet Status" value={`${data.trucks_available ?? 0} idle`} sub={`${data.active_trucks} on jobs`} tone="good" />
        <KpiCard testid="kpi-plant-status" icon={Bell} label="Plant Status" value={(data.plant_status || "idle").toUpperCase()} sub="Live telemetry" tone={data.plant_status === "operational" ? "good" : "warn"} />
      </div>

      {/* ── Today's Orders (top, editable) ──────────────────────────────── */}
      <Card className="card-v2 p-4" data-testid="dashboard-orders-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-primary-orange" />
            <div className="label-tag">Today's Jobs · {viewDate === today ? "Today" : viewDate}</div>
            <span className="mono text-[10px] uppercase text-muted-industrial">{sortedJobs.length} total · {data.jobs_remaining} open</span>
          </div>
        </div>
        {sortedJobs.length === 0 && (
          <div className="text-sm text-muted-industrial border border-dashed border-industrial rounded-lg p-6 text-center">
            No orders scheduled for {viewDate}.
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          {sortedJobs.map((j) => {
            const isCompleted = j.status === "completed";
            const hasExtras = !!(j.extras && j.extras.trim());
            const mc = mixColor(j.mix_code);
            const pct = Math.min(100, (j.delivered_m3 / j.quantity_m3) * 100);
            return (
              <div
                key={j.id}
                data-testid={`dash-job-row-${j.id}`}
                data-status={j.status}
                className={`relative bg-surface border border-industrial rounded-lg hover:border-primary-orange transition-all overflow-hidden ${isCompleted ? "opacity-55" : ""}`}
              >
                <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: mc.hex }} />
                {hasExtras && !isCompleted && (
                  <div className="absolute right-0 top-0 bottom-0 w-1" style={{ background: "#FBBF24" }} title={`Extras: ${j.extras}`} />
                )}
                <div className="flex items-center gap-3 pl-4 pr-4 py-2.5">
                  <span className="mono text-xs text-primary-orange w-14 shrink-0">{j.time_required || "—"}</span>
                  <span className="mono text-[10px] uppercase tracking-widest px-2 py-0.5 border shrink-0" style={{ borderColor: mc.border, color: mc.hex, background: mc.bg }}>
                    {j.mix_code}
                  </span>
                  <span className="heading text-sm font-bold truncate flex-1">{j.customer_name}</span>
                  {hasExtras && (
                    <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-[#FBBF24] text-[#FBBF24] inline-flex items-center gap-1 shrink-0" title={j.extras}>
                      <Sparkles className="w-2.5 h-2.5" /> EXTRAS
                    </span>
                  )}
                  {isCompleted && (
                    <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-primary-orange text-primary-orange shrink-0">CLOSED</span>
                  )}
                  <div className="hidden md:flex items-center gap-2 w-36 shrink-0">
                    <div className="h-1 bg-surface-2 flex-1">
                      <div className="h-full bg-primary-orange" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="mono text-[10px] text-muted-industrial w-16 text-right">{j.delivered_m3}/{j.quantity_m3}m³</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isCompleted && (
                      <>
                        <button
                          data-testid={`dash-edit-${j.id}`}
                          onClick={() => openEdit(j)}
                          className="mono text-[10px] uppercase text-muted-industrial hover:text-primary-orange inline-flex items-center gap-1"
                        >
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                        <button
                          data-testid={`dash-close-${j.id}`}
                          onClick={() => closeJob(j)}
                          className="mono text-[10px] uppercase text-ink hover:text-primary-orange inline-flex items-center gap-1"
                        >
                          <CheckCircle2 className="w-3 h-3" /> Close
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-2">
          <AIDispatchPanel plantId={plantId} />
        </div>

        <Card className="card-v2 p-5 lg:col-span-1" data-testid="current-batches-card">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-primary-orange" />
            <div className="label-tag">Current Batches</div>
          </div>
          {data.current_batches.length === 0 && (
            <div className="text-sm text-muted-industrial">No batches queued.</div>
          )}
          <div className="space-y-3">
            {data.current_batches.map((b) => (
              <div key={b.id} className="border border-industrial rounded-lg p-3">
                <div className="flex justify-between">
                  <span className="mono font-bold">{b.mix_code}</span>
                  <span className="mono text-primary-orange text-xs uppercase">{b.status}</span>
                </div>
                <div className="text-xs text-muted-industrial mt-1">Truck {b.truck_number} · {b.quantity_m3} m³</div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="card-v2 p-5 lg:col-span-1" data-testid="inventory-alerts-card">
          <div className="flex items-center gap-2 mb-4">
            <PackageOpen className="w-4 h-4 text-primary-orange" />
            <div className="label-tag">Material Levels · Low Stock</div>
          </div>
          {data.inventory_alerts.length === 0 && <div className="text-sm text-muted-industrial">All materials healthy.</div>}
          <div className="space-y-2">
            {data.inventory_alerts.map((i) => {
              const bulk = ["cement","sand","man_dust","aggregate_7mm","aggregate_10mm","aggregate_14mm","aggregate_20mm"].includes(i.category);
              const qty = bulk ? (i.quantity || 0) / 1000 : i.quantity;
              const unit = bulk ? "t" : i.unit;
              return (
                <div key={i.id} className="flex justify-between border-b border-industrial pb-2 text-sm">
                  <span>{i.name}</span>
                  <span className="mono text-primary-orange">{qty.toLocaleString(undefined, { maximumFractionDigits: bulk ? 2 : 0 })} {unit}</span>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="card-v2 p-5 lg:col-span-1" data-testid="recent-deliveries-card">
          <div className="flex items-center gap-2 mb-4">
            <Truck className="w-4 h-4 text-primary-orange" />
            <div className="label-tag">Recent Deliveries</div>
          </div>
          {data.recent_deliveries.length === 0 && <div className="text-sm text-muted-industrial">No deliveries yet.</div>}
          <div className="space-y-2">
            {data.recent_deliveries.map((d) => (
              <div key={d.id} className="border-b border-industrial pb-2 text-xs">
                <div className="flex justify-between">
                  <span className="mono font-bold">{d.docket_no}</span>
                  <span className="mono text-primary-orange">{d.status}</span>
                </div>
                <div className="text-muted-industrial mt-0.5">{d.customer_name} · {d.mix_code} · {d.quantity_m3} m³</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editJob} onOpenChange={(v) => { if (!v) setEditJob(null); }}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-lg" data-testid="dash-edit-dialog">
          <DialogHeader><DialogTitle className="heading uppercase">Edit Order</DialogTitle></DialogHeader>
          {editJob && (
            <div className="space-y-3">
              <div className="text-xs text-muted-industrial">
                <span className="mono text-primary-orange">{editJob.customer_name}</span> · MIX {editJob.mix_code}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="label-tag">Date</Label>
                  <Input data-testid="dash-edit-date" type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} className="rounded-none bg-transparent border-industrial mono" />
                </div>
                <div>
                  <Label className="label-tag">Qty (m³)</Label>
                  <Input data-testid="dash-edit-qty" type="number" step="0.5" value={editForm.quantity_m3} onChange={(e) => setEditForm({ ...editForm, quantity_m3: parseFloat(e.target.value) })} className="rounded-none bg-transparent border-industrial mono" />
                </div>
                <div>
                  <Label className="label-tag">Time</Label>
                  <Input data-testid="dash-edit-time" type="time" value={editForm.time_required} onChange={(e) => setEditForm({ ...editForm, time_required: e.target.value })} className="rounded-none bg-transparent border-industrial mono" />
                </div>
              </div>
              <div>
                <Label className="label-tag">PO Number</Label>
                <Input data-testid="dash-edit-po" value={editForm.po_number} onChange={(e) => setEditForm({ ...editForm, po_number: e.target.value })} className="rounded-none bg-transparent border-industrial mono" />
              </div>
              <div>
                <Label className="label-tag flex items-center gap-1.5" style={{ color: "#FBBF24" }}>
                  <Sparkles className="w-3 h-3" /> Extras
                </Label>
                <Input data-testid="dash-edit-extras" value={editForm.extras} onChange={(e) => setEditForm({ ...editForm, extras: e.target.value })} placeholder="e.g. Fibre mesh, pump" className="rounded-none bg-transparent border-industrial mono" />
              </div>
              {editForm.date !== editJob.date && (
                <div className="text-[10px] mono uppercase text-primary-orange border-l-2 border-primary-orange pl-2">
                  Moving from {editJob.date} → {editForm.date}. This order will disappear from {editJob.date}&apos;s board.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button data-testid="dash-edit-save" onClick={saveEdit} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
